// Словари и расчёты раздела «Банк и бухгалтерия». Без React — чтобы их можно было
// проверить отдельно от отрисовки (tests/pbh.test.tsx).
//
// Здесь же собраны оговорки, которые экран ОБЯЗАН показать: числа выписки читаются неверно
// без них. Главная — «оплата по сделке 109300» это гипотеза портала, найденная в тексте
// назначения, и у 77 операций этот «номер сделки» равен сумме платежа.

export const РАЗМЕР_СТРАНИЦЫ = 50

/**
 * Состояние разбора операции в портале.
 *
 * Четыре статуса — это четыре разных вопроса к бухгалтеру, и мешать их нельзя:
 * 'none' — прошли и ни к чему не привязаны (1 381 операция на 28.07.2026, ради них раздел);
 * 'hyp'  — машина ПРЕДПОЛОЖИЛА привязку и ждёт подтверждения (7 797);
 * 'auto' — привязано правилом без участия человека (3 619);
 * 'done' — разнесено руками (1).
 */
export function видРазбора(status: string | null | undefined): {
  ярлык: string
  смысл: string
  класс: string
} {
  switch (status) {
    case 'none':
      return {
        ярлык: 'не привязано',
        смысл: 'деньги прошли по счёту и не связаны ни со сделкой, ни со счётом',
        класс: 'badge lose',
      }
    case 'hyp':
      return {
        ярлык: 'гипотеза',
        смысл: 'машина предположила привязку по тексту назначения — бухгалтер не подтвердил',
        класс: 'badge work',
      }
    case 'auto':
      return {
        ярлык: 'привязано правилом',
        смысл: 'разнесено автоматически по контрагенту или шаблону, без участия человека',
        класс: 'badge',
      }
    case 'done':
      return {
        ярлык: 'разнесено',
        смысл: 'бухгалтер разнёс операцию руками',
        класс: 'badge won',
      }
    default:
      // Незнакомый статус показываем как есть: портал волен завести новый, и «—» на его
      // месте читалось бы как «статуса нет».
      return {
        ярлык: status || 'статус не указан',
        смысл: 'статус портала неизвестен CRM',
        класс: 'badge',
      }
  }
}

/** Приход или расход. Знак у суммы в выписке не хранится — только направление. */
export function видНаправления(dir: string | null | undefined): {
  ярлык: string
  знак: string
  класс: string
} {
  if (dir === 'in') return { ярлык: 'приход', знак: '+', класс: 'badge won' }
  if (dir === 'out') return { ярлык: 'расход', знак: '−', класс: 'badge' }
  return { ярлык: dir || 'направление не указано', знак: '', класс: 'badge lose' }
}

/** Вердикт сальдового аудита по счёту бухучёта. */
export function видВердикта(status: string | null | undefined): {
  ярлык: string
  класс: string
  плохо: boolean
} {
  if (status === 'err') return { ярлык: 'ошибка', класс: 'badge lose', плохо: true }
  if (status === 'warn') return { ярлык: 'внимание', класс: 'badge work', плохо: true }
  if (status === 'ok') return { ярлык: 'в порядке', класс: 'badge won', плохо: false }
  return { ярлык: status || 'вердикта нет', класс: 'badge', плохо: false }
}

/** Тип счёта бухучёта словом: от него зависит, что считать «красным» сальдо. */
export function видСчёта(acct_type: string | null | undefined): string {
  if (acct_type === 'Active') return 'активный'
  if (acct_type === 'Passive') return 'пассивный'
  if (acct_type === 'ActivePassive') return 'активно-пассивный'
  return acct_type || 'тип не указан'
}

/** Действие в журнале отметок аудита. */
export function видДействия(action: string | null | undefined): { ярлык: string; класс: string } {
  if (action === 'fixed') return { ярлык: 'разобрано', класс: 'badge won' }
  if (action === 'reopen') return { ярлык: 'открыто заново', класс: 'badge lose' }
  if (action === 'confirmed') return { ярлык: 'подтверждено', класс: 'badge' }
  return { ярлык: action || 'действие не указано', класс: 'badge' }
}

/**
 * Насколько догадке портала о сделке можно верить. Три состояния, и они РАЗНЫЕ.
 *
 * Почему это здесь, а не «показать ссылку, если есть номер». На живых данных
 * из 1 580 гипотез у 77 «номер сделки» РАВЕН сумме платежа (замер 28.07.2026): назначение
 * «…Сумма 109300-00» дало «сделку 109300», и 53 таких числа существуют в Bitrix как
 * настоящие чужие сделки.
 * То есть ядро вернёт и номер, и НАЗВАНИЕ живой сделки — а связи никакой нет. Ссылка без
 * этой проверки была бы самой уверенной ложью на экране.
 */
export function довериеКСделке(r: {
  deal_guess: number | null
  deal_guess_is_amount: boolean | null
  deal_guess_title: string | null
}): { есть: boolean; ложная: boolean; ярлык: string; смысл: string } {
  if (r.deal_guess === null) {
    return { есть: false, ложная: false, ярлык: '', смысл: '' }
  }
  if (r.deal_guess_is_amount) {
    return {
      есть: true,
      ложная: true,
      ярлык: 'номер совпал с суммой платежа',
      смысл: 'портал взял число из «Сумма ' + r.deal_guess + '-00» и принял его за номер '
        + 'сделки. Связи с этой сделкой скорее всего нет — проверять руками',
    }
  }
  return {
    есть: true,
    ложная: false,
    ярлык: 'гипотеза портала',
    смысл: 'номер найден в тексте назначения платежа, бухгалтер его не подтверждал',
  }
}

/**
 * Две кучи внутри непривязанных денег. Складывать их в одно число нельзя: у первой есть
 * зацепка (номер счёта в назначении) и разбирать её надо в 1С, у второй нет ничего.
 */
export function кучиНеразнесённого(t: {
  n: number | null
  with_refs_n: number | null
  with_refs_rub: number | null
  no_refs_n: number | null
  no_refs_rub: number | null
}): { сЗацепкой: number; безЗацепки: number; сходится: boolean } {
  const с = t.with_refs_n ?? 0
  const без = t.no_refs_n ?? 0
  return { сЗацепкой: с, безЗацепки: без, сходится: с + без === (t.n ?? 0) }
}

/**
 * Тренд по месяцам: становится ли лучше. Считаем по ДОЛЕ, а не по числу операций —
 * в июле операций всего 206 против 1 832 в апреле (замер 28.07.2026), и «5 против 77»
 * само по себе не значит ничего.
 *
 * Последний месяц исключаем из вывода о направлении: выписка обрывается 13.07.2026,
 * и неполный месяц всегда выглядит лучше полного.
 */
export function трендНеразнесённого(
  месяцы: { month: string; share_pct: number | null }[],
): { первый: number | null; последнийПолный: number | null; лучше: boolean | null } {
  const с = месяцы.filter((м) => м.share_pct !== null) as { month: string; share_pct: number }[]
  if (с.length < 2) return { первый: null, последнийПолный: null, лучше: null }
  const первый = с[0].share_pct
  // Отбрасываем последний месяц как неполный — но только если их больше двух,
  // иначе сравнивать станет нечего и честнее не утверждать ничего.
  const полные = с.length > 2 ? с.slice(0, -1) : с
  const последний = полные[полные.length - 1].share_pct
  return { первый, последнийПолный: последний, лучше: последний < первый }
}

/** Месяц «ГГГГ-ММ» словами: «2026-07» → «июль 2026». */
const МЕСЯЦЫ = ['январь', 'февраль', 'март', 'апрель', 'май', 'июнь', 'июль', 'август',
  'сентябрь', 'октябрь', 'ноябрь', 'декабрь']

export function месяцСловами(m: string | null | undefined): string {
  const s = String(m ?? '')
  const п = /^(\d{4})-(\d{2})$/.exec(s)
  if (!п) return s || '—'
  const i = Number(п[2]) - 1
  return `${МЕСЯЦЫ[i] ?? п[2]} ${п[1]}`
}

/**
 * Насколько данные устарели. Возвращает худшую по разделу сущность: человек смотрит на
 * раздел целиком, а не на тринадцать таблиц. То же правило, что в lib/p73x.ts.
 */
export function свежесть(
  список: { entity: string; age_min: number | null }[] | undefined,
): { entity: string; age_min: number } | null {
  const с = (список ?? []).filter((x) => x.age_min !== null) as {
    entity: string
    age_min: number
  }[]
  if (!с.length) return null
  return с.reduce((a, b) => (b.age_min > a.age_min ? b : a))
}

/** Короткое имя сущности синка для показа человеку. */
export const ИМЯ_СУЩНОСТИ: Record<string, string> = {
  'pbh:rows': 'выписка',
  'pbh:rules': 'правила разнесения',
  'pbh:uploads': 'журнал заливок',
  'pbh:chk_fix': 'отметки аудита',
  'pbh:chk_fix_log': 'журнал аудита',
  'pbh:audit_snap': 'снимки аудита',
  'pbh:balance_audit': 'сальдо счетов',
  'pbh:seen': 'отработанные документы',
  'pbh:s1c_rules': 'правила разноски 1С',
  'pbh:users': 'учётки портала',
  'pbh:user_map': 'связка с Bitrix',
}

/** Понятное имя счётчика аудита 1С. Ключи приходят из data снимка. */
export const ИМЯ_СЧЁТЧИКА: Record<string, string> = {
  unposted: 'непроведённые документы',
  old_invoices: 'старые счета',
  dup_nomen: 'дубли номенклатуры',
  post_no_sf: 'поступления без СФ',
  dup_incoming: 'дубли поступлений',
  contragents: 'проблемные контрагенты',
  manual_ops: 'ручные операции',
  future: 'документы с будущей датой',
  bad_inn: 'некорректные ИНН',
  vat_adv: 'НДС с авансов',
  zero_sum: 'нулевые суммы',
  real_no_sf: 'реализации без СФ',
  sf_no_incoming: 'СФ без поступления',
  posted_deleted: 'проведённые помеченные на удаление',
}

/**
 * Оговорки, без которых числа раздела читаются неверно. Тексты приходят из ядра
 * (refs.data_notes) — здесь только порядок и то, какие показывать всегда.
 *
 * Первые две — не «дополнительная информация», а условие правильного чтения экрана:
 * без них «оплата по сделке 109300» выглядит готовой связью, а её нет.
 */
export const ВАЖНЫЕ_ОГОВОРКИ = [
  'deal_guess_is_a_guess',
  'refs_do_not_match',
  'decision_lags',
  'status_is_a_snapshot',
  'gross_turnover',
  'closed_tables',
  'counterparty_account_inside_name',
  'second_account',
]
